import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Pagination } from 'enums';
import { useAppSelector, useTypedDispatch } from 'hooks';
import {
  fetchProperties,
  PropertyType,
  selectProperties,
  selectPropertiesPagination,
  UserType,
} from 'state';
import { searchProperties, setPropertiesPagination } from 'state/actions';
import { ISelectOption } from 'ui-kit/Select/Select';
import { getOptions } from 'utils';

export type SelectPropertiesType = {
  propertiesData: PropertyType[];
  propertiesIds: string;
  selectOptions: ISelectOption[];
  selectedValue: ISelectOption[];
  setSelectedValue: React.Dispatch<React.SetStateAction<ISelectOption[]>>;
  onChangeSelect: (properties: any) => void;
  onDeleteAllClick: () => void;
  onDeleteIconClick: (id: string) => void;
  onFocusSelect: () => void;
  onInputChangeSelect: (value?: string) => void;
  onMenuScrollToBottom: (e?: MouseEvent) => void;
};

type UseSelectPropertiesType = {
  user?: UserType;
};

export const useSelectProperties = ({
  user,
}: UseSelectPropertiesType): SelectPropertiesType => {
  const [selectedValue, setSelectedValue] = useState<any[]>(
    user ? getOptions(user?.properties, 'id', 'address') : [],
  );
  const dispatch = useTypedDispatch();
  const properties = useAppSelector(selectProperties);
  const { page, count } = useAppSelector(selectPropertiesPagination);

  useEffect(() => {
    dispatch(fetchProperties(false));
  }, [selectedValue, dispatch]);

  const propertiesData = useMemo(() => {
    return properties?.filter(property =>
      selectedValue?.map(item => item.value).includes(property.id),
    );
  }, [properties, selectedValue]);

  const propertiesIds = useMemo(() => {
    return selectedValue?.map(item => item.value).join();
  }, [selectedValue]);

  const selectOptions = useMemo(() => {
    return user
      ? getOptions(properties, 'id', 'address').filter(
          property => !user.properties?.map(({ id }) => id).includes(property.value),
        )
      : getOptions(properties, 'id', 'address');
  }, [properties, user]);

  const onChangeSelect = (properties: any) => {
    setSelectedValue(properties);
  };

  const onInputChangeSelect = useCallback(
    (value?: string) => {
      dispatch(searchProperties(value || ''));
      dispatch(fetchProperties(false));
    },
    [dispatch],
  );

  const onFocusSelect = () => {
    dispatch(fetchProperties(false));
  };

  const onDeleteIconClick = (id: string) => {
    setSelectedValue(selectedValue?.filter(property => property.value !== id));
  };

  const onDeleteAllClick = () => {
    setSelectedValue([]);
  };

  const onMenuScrollToBottom = (e?: MouseEvent) => {
    if (e) e.stopPropagation();
    if (count && properties.length < count && page * Pagination.TAKE < count) {
      dispatch(setPropertiesPagination({ page: page + 1 }));
      dispatch(fetchProperties(true));
    }
  };

  return {
    propertiesData,
    propertiesIds,
    selectOptions,
    onChangeSelect,
    onInputChangeSelect,
    onFocusSelect,
    onDeleteIconClick,
    selectedValue,
    setSelectedValue,
    onDeleteAllClick,
    onMenuScrollToBottom,
  };
};
